import { bondRetrievalPageConfig } from "./bondRetrievalPageConfig";
import {
  guardianCitizenshipForPayload,
  normalizeGuardiansForPayload,
} from "../../../features/bonds/shared/guardianCitizenshipHelpers";
import {
  getBondPayloadKindConfig,
  getRetrievePrincipalCount,
} from "../../../features/bonds/shared/bondPayloadConfig";

/** Approve/review UI count when DB count lags behind principal rows (VA legacy typo field included). */
export function resolvePrincipalNumberForRetrieve(
  storedCount,
  principalRowsLength
) {
  const stored = Number(storedCount);
  const rows = Number(principalRowsLength);
  const resolved = Math.max(
    Number.isFinite(stored) && stored > 0 ? stored : 0,
    Number.isFinite(rows) && rows > 0 ? rows : 0
  );
  return String(resolved > 0 ? resolved : 1);
}

export const mapApiVaPrincipalToGuardian = (guardian) => ({
  fid_id: guardian.fid_id,
  name: guardian.name,
  citizenship: guardian.citizenship,
  custom_citizenship: guardian.custom_citizenship,
  country: guardian.country,
  custom_country: guardian.custom_country,
  relationship: guardian.relationship,
  address: guardian.address,
  guardian_street_address_2: "",
  city: guardian.city,
  state: guardian.state,
  zip_code: guardian.zip_code,
  occupation: guardian.occupation,
  ssn: guardian.ssn,
  is_professional_guardian: guardian.is_professional_guardian ? "Yes" : "No",
  professional_guardian: guardian.professional_guardian,
  is_attorney: guardian.is_attorney ? "Yes" : "No",
  attorney: guardian.attorney,
});

export const mapApiTrusteeToGuardian = (guardian) => ({
  trustee_id: guardian.trustee_id,
  name: guardian.name,
  citizenship: guardian.citizenship,
  custom_citizenship: guardian.custom_citizenship,
  country: guardian.country,
  custom_country: guardian.custom_country,
  relationship: guardian.relationship,
  address: guardian.address,
  guardian_street_address_2: "",
  city: guardian.city,
  state: guardian.state,
  zip_code: guardian.zip_code,
  occupation: guardian.occupation,
  ssn: guardian.ssn,
  is_professional_guardian: guardian.is_professional_guardian ? "Yes" : "No",
  professional_guardian: guardian.professional_guardian,
  is_attorney: guardian.is_attorney ? "Yes" : "No",
  is_attorney_home: guardian.is_attorney_home ? "Yes" : "No",
  attorney: guardian.attorney,
});

export const mapApiGuardianToGuardian = (guardian) => ({
  guardian_id: guardian.guardian_id,
  guardian_name: guardian.guardian_name || "",
  guardian_citizenship: guardianCitizenshipForPayload(guardian.guardian_citizenship),
  custom_guardian_citizenship: guardian.custom_guardian_citizenship,
  country: guardian.country,
  custom_country: guardian.custom_country,
  guardian_relationship_to_ward: guardian.guardian_relationship_to_ward,
  guardian_street_address_1: guardian.guardian_street_address_1,
  guardian_street_address_2: guardian.guardian_street_address_2,
  guardian_city: guardian.guardian_city,
  guardian_state: guardian.guardian_state,
  guardian_zip: guardian.guardian_zip,
  guardian_occupation: guardian.guardian_occupation,
  ssn: guardian.ssn,
  is_professional_guardian: guardian.is_professional_guardian ? "Yes" : "No",
  professional_guardian: guardian.professional_guardian,
  is_attorney: guardian.is_attorney ? "Yes" : "No",
  is_attorney_home: guardian.is_attorney_home ? "Yes" : "No",
  attorney: guardian.attorney,
});

export const mapApiPersonalRepToGuardian = (personal_rep) => ({
  pr_id: personal_rep.pr_id,
  name: personal_rep.name || "",
  citizenship: personal_rep.citizenship,
  custom_citizenship: personal_rep.custom_citizenship,
  country: personal_rep.country,
  custom_country: personal_rep.custom_country,
  relationship: personal_rep.relationship,
  address: personal_rep.address,
  city: personal_rep.city,
  state: personal_rep.state,
  zip_code: personal_rep.zip_code,
  email: personal_rep.email,
  occupation: personal_rep.occupation,
  ssn: personal_rep.ssn,
  is_professional_guardian: personal_rep.is_professional_guardian ? "Yes" : "No",
  professional_guardian: personal_rep.professional_guardian,
  is_attorney: personal_rep.is_attorney ? "Yes" : "No",
  is_attorney_home: personal_rep.is_attorney_home ? "Yes" : "No",
  attorney: personal_rep.attorney,
});

/** @param {{ resolveVaObligeeInformation: (apiData:any, prevState:any)=>any }} ctx */
export function mergeVaRetrieveResponse(prevState, data, { resolveVaObligeeInformation }) {
  return {
    ...prevState,
    basicInformation: {
      firstName: data.vabond?.beneficiary_first_name || "",
      lastName: data.vabond?.beneficiary_last_name || "",
      middleName: data.vabond?.beneficiary_middle_name || "",
      suffix: data.vabond?.beneficiary_suffix || "",
      aliases: data.vabond?.beneficiary_alias || "",
      bondSubCategory: data.bond_sub_category || "",
      dateOfBirth: data.vabond?.beneficiary_date_of_birth || "",
      bondAmount: data.bond_amount || "",
      premiumAmount: data.premium || "",
      courtCaseNumber: data.court_case_number || "",
      PrincipalNumber: resolvePrincipalNumberForRetrieve(
        data.vabond?.number_of_principals ??
          data.vabond?.number_of_prinicipals,
        data.principals?.length
      ),
      is_esignatures: data?.is_esignatures
        ? "Yes"
        : "No",
      county: data.county || "N/A",
    },
    generalInformation: {
      ongoingBusiness: data.vabond
        ?.fiduciary_operate_beneficiarys_business
        ? "Yes"
        : "No",
      indebtedGuardians: data.vabond?.prinicipal_indebited_to_estate
        ? "Yes"
        : "No",
      didLastWillAndTestament: data.vabond
        ?.beneficiary_execute_last_will_and_testament
        ? "Yes"
        : "No",
      replacingFiduciary: data.vabond?.prinicipal_replace_prior_fiduciary
        ? "Yes"
        : "No",
      replacingPriorBond: data.vabond?.bond_replace_prior_bond
        ? "Yes"
        : "No",
      demandedByInterestedParty: data.vabond?.replaced_by_non_court
        ? "Yes"
        : "No",
      jointControlExercised: data.vabond?.joint_control_exercised
        ? "Yes"
        : "No",
      supplementalNeedsTrust: data.vabond
        ?.supplemental_needs_trust_created
        ? "Yes"
        : "No",
      requestForAuthorization: data.vabond?.credit_report_allowed
        ? "Yes"
        : "No",
      outstandingLawsuit: data.vabond?.is_lawsuits_judgements_liens
        ? "Yes"
        : "No",
      bankruptOrInsolvent: data.vabond?.is_bankrupt_insolvent
        ? "Yes"
        : "No",
      isDeclined: data.vabond?.is_declined ? "Yes" : "No",
      explanations: {
        ongoingBusiness:
          data.vabond?.fiduciary_business_explanation || "",
        indebtedGuardians:
          data.vabond?.prinicipal_indebted_explanation || "",
        didLastWillAndTestament:
          data.vabond
            ?.beneficiary_execute_last_will_and_testament_explaination ||
          "",
        replacingFiduciary:
          data.vabond?.prinicipal_replace_prior_fiduciary_explaination ||
          "",
        replacingPriorBond:
          data.vabond?.bond_replace_prior_bond_explaination || "",
        jointControlExercised:
          data.vabond?.joint_control_explanation || "",
        supplementalNeedsTrust:
          data.vabond?.supplemental_needs_trust_explanation || "",
        demandedByInterestedParty:
          data.vabond?.required_non_court_explanation || "",
        outstandingLawsuitExplanation:
          data.vabond?.is_lawsuits_judgements_liens_explanation || "",
        bankruptOrInsolventExplanation:
          data.vabond?.is_bankrupt_insolvent_explanation || "",
        isDeclined: data.vabond?.is_declined_explanation || "",
      },
    },
    guardianDetails: {
      guardians: data.principals.map(mapApiVaPrincipalToGuardian),
    },
    attorneyInformation: {
      attorneyInvolved: data.law_firm || data.guest_lawfirm_name ? "Yes" : "No",
      lawFirm: data.law_firm,
      lawFirmAddress: data.law_firm_location,
      attorneyOfRecord: data.law_firm_user,
      guest_lawfirm_name: data.guest_lawfirm_name,
      guest_lawfirmaddress_name: data.guest_lawfirmaddress_name,
      guest_attorney_name: data.guest_attorney_name,
      is_guest: data.is_guest,
      referral_source: data.referral_source,
      referred_by: data.referred_by,
    },
    obligeeInformation: resolveVaObligeeInformation(data, prevState),
    requestorInformation: {
      email: data.requester_user_email || "No email",
      phoneNumber: data.requester_phone_number || "No phone number",
      firstName: data.requester_name,
    },
    documentUpload: {
      lastWillFileName:
        data.vabond?.copy_of_last_will_and_testament ||
        "No file uploaded",
      lastWillLink:
        data.vabond?.copy_of_last_will_and_testament_link || "",
      otherDocumentFileName:
        data.vabond?.copy_of_supplemental_needs_trust ||
        "No file uploaded",
      otherDocumentLink:
        data.vabond?.copy_of_supplemental_needs_trust_link || "",
      courtOrderFileName:
        data.vabond?.copy_of_court_order || "No file uploaded",
      courtOrderLink: data.vabond?.copy_of_court_order_link || "",
      bondAppFileName: data.first_document_name || "No file uploaded",
      bondAppLink: data.first_document_url || "",
      bondFileName: data.second_document_name || "No file uploaded",
      bondLink: data.second_document_url || "",
      indemnityFileName: data.third_document_name || "No file uploaded",
      indemnityLink: data.third_document_url || "",
      cancellationDocumentFileName:
        data.vabond?.cancellation_document || "No file uploaded",
      cancellationDocumentLink:
        data.vabond?.cancellation_document_link || "",
      endorsementDocumentFileName:
        data.vabond?.endorsement_document || "No file uploaded",
      endorsementDocumentLink:
        data.vabond?.endorsement_document_link || "",
      endorsementPOADocumentFileName:
        data.vabond?.endorsement_poa_document || "No file uploaded",
      endorsementPOADocumentLink:
        data.vabond?.endorsement_poa_document_link || "",
      creditDocumentFileName:
        data.vabond?.credit_report_document || "No file uploaded",
      creditDocumentLink: data.vabond?.credit_report_document_link || "",
      fcraDocumentFileName:
        data.vabond?.fcra_document || "No file uploaded",
      fcraDocumentLink: data.vabond?.fcra_document_link || "",
      note: data.vabond?.notes || "",
    },
    billingInformation: {
      billing_entity: data.vabond?.billing_entity || "Unknown",
      billing_lawfirmaddress_name:
        data.vabond?.billing_lawfirmaddress_name || "",
      billing_principal_name:
        data.vabond?.billing_principal_name || "Unknown",
      billing_other_name: data.vabond?.billing_other_name || "",
      billing_other_address: data.vabond?.billing_other_address || "",
      billing_other_relationship:
        data.vabond?.billing_other_relationship || "",
      billing_other_city: data.vabond?.billing_other_city,
      billing_other_state: data.vabond?.billing_other_state,
      billing_other_zip: data.vabond?.billing_other_zip,
      billing_method: data.vabond?.billing_method ?? data.billing_method ?? "",
      custNo: data.vabond?.other_custno || "",
    },
    bondNumber: {
      bond_number: data.bond_number,
    },
  };
}

/** @param {{ resolveObligeeInformationFromResponse: (apiData:any)=>any }} ctx */
export function mergeTrusteeRetrieveResponse(prevState, data, { resolveObligeeInformationFromResponse }) {
  return {
    ...prevState,
    basicInformation: {
      legalName: data.trustee_bond?.legal_name || "",
      trustName: data.trustee_bond?.trust_name || "",
      bondSubCategory: data.bond_sub_category || "",
      dateOfBirth: data.trustee_bond?.dob || "",
      bondAmount: Number.parseFloat(data.bond_amount).toString(),
      premiumAmount: data.premium,
      courtCaseNumber: data.court_case_number || "",
      PrincipalNumber: resolvePrincipalNumberForRetrieve(
        data.trustee_bond?.number_of_principals,
        data.trustees?.length
      ),
      is_esignatures: data?.is_esignatures
        ? "Yes"
        : "No",
      county: data.county,
    },
    generalInformation: {
      allUSUKCanada: data.trustee_bond?.all_us_canadian_uk_citizens
        ? "Yes"
        : "No",
      ongoingBusiness: data.trustee_bond?.any_business_operated
        ? "Yes"
        : "No",
      indebtedGuardians: data.trustee_bond?.indebted_state ? "Yes" : "No",
      replacingPriorBond: data.trustee_bond?.is_replaced_prior
        ? "Yes"
        : "No",
      jointControlExercised: data.trustee_bond?.joint_control_exercised
        ? "Yes"
        : "No",
      supplementalNeedsTrust: data.trustee_bond?.is_supplemental_needs
        ? "Yes"
        : "No",
      requestForAuthorization: data.trustee_bond?.credit_report_allowed
        ? "Yes"
        : "No",
      explanations: {
        ongoingBusiness:
          data.trustee_bond?.any_business_operated_explanation || "",
        indebtedGuardians:
          data.trustee_bond?.indebted_state_explanation || "",
        replacingPriorBond:
          data.trustee_bond?.is_replaced_prior_explanation || "",
        supplementalNeedsTrust:
          data.trustee_bond?.supplemental_needs_trust_explanation || "",
      },
    },
    guardianDetails: {
      guardians: data.trustees.map(mapApiTrusteeToGuardian),
    },
    attorneyInformation: {
      attorneyInvolved: data.law_firm || data.guest_lawfirm_name ? "Yes" : "No",
      lawFirm: data.law_firm,
      lawFirmAddress: data.law_firm_location,
      attorneyOfRecord: data.law_firm_user,
      guest_lawfirm_name: data.guest_lawfirm_name,
      guest_lawfirmaddress_name: data.guest_lawfirmaddress_name,
      guest_attorney_name: data.guest_attorney_name,
      is_guest: data.is_guest,
      referral_source: data.referral_source,
      referred_by: data.referred_by,
    },
    obligeeInformation: resolveObligeeInformationFromResponse(data),
    requestorInformation: {
      attorneyInvolved: data.law_firm ? "Yes" : "No",
      email: data.requester_user_email || "No email",
      phoneNumber: data.requester_phone_number || "No phone number",
      firstName: data.requester_name,
    },
    documentUpload: {
      lastWillFileName:
        data.trustee_bond?.copy_of_last_will_and_testament ||
        "No file uploaded",
      lastWillLink:
        data.trustee_bond?.copy_of_last_will_and_testament_link || "",
      otherDocumentFileName:
        data.trustee_bond?.copy_of_supplemental_needs_trust ||
        "No file uploaded",
      otherDocumentLink:
        data.trustee_bond?.copy_of_supplemental_needs_trust_link || "",
      courtOrderFileName:
        data.trustee_bond?.copy_of_court_order || "No file uploaded",
      courtOrderLink: data.trustee_bond?.copy_of_court_order_link || "",
      bondAppFileName: data.first_document_name || "No file uploaded",
      bondAppLink: data.first_document_url || "",
      bondFileName: data.second_document_name || "No file uploaded",
      bondLink: data.second_document_url || "",
      indemnityFileName: data.third_document_name || "No file uploaded",
      indemnityLink: data.third_document_url || "",
      cancellationDocumentFileName:
        data.trustee_bond?.cancellation_document || "No file uploaded",
      cancellationDocumentLink:
        data.trustee_bond?.cancellation_document_link || "",
      endorsementDocumentFileName:
        data.trustee_bond?.endorsement_document || "No file uploaded",
      endorsementDocumentLink:
        data.trustee_bond?.endorsement_document_link || "",
      endorsementPOADocumentFileName:
        data.trustee_bond?.endorsement_poa_document || "No file uploaded",
      endorsementPOADocumentLink:
        data.trustee_bond?.endorsement_poa_document_link || "",
      creditDocumentFileName:
        data.trustee_bond?.credit_report_document || "No file uploaded",
      creditDocumentLink:
        data.trustee_bond?.credit_report_document_link || "",
      fcraDocumentFileName:
        data.trustee_bond?.fcra_document || "No file uploaded",
      fcraDocumentLink:
        data.trustee_bond?.fcra_document_link || "",
      note: data.trustee_bond?.notes || "",
    },
    billingInformation: {
      billing_entity: data.trustee_bond?.billing_entity || "Unknown",
      billing_lawfirmaddress_name:
        data.trustee_bond?.billing_lawfirmaddress_name || "",
      billing_principal_name:
        data.trustee_bond?.billing_principal_name || "Unknown",
      billing_other_name: data.trustee_bond?.billing_other_name || "",
      billing_other_address:
        data.trustee_bond?.billing_other_address || "",
      billing_other_relationship:
        data.trustee_bond?.billing_other_relationship || "",
      billing_other_city: data.trustee_bond?.billing_other_city,
      billing_other_state: data.trustee_bond?.billing_other_state,
      billing_other_zip: data.trustee_bond?.billing_other_zip,
      billing_method: data.trustee_bond?.billing_method ?? data.billing_method ?? "",
      custNo: data.trustee_bond?.other_custno || "",
    },
    bondNumber: {
      bond_number: data.bond_number,
    },
  };
}

/** @param {{ resolveObligeeInformationFromResponse: (apiData:any)=>any }} ctx */
export function mergeGuardianshipRetrieveResponse(prevState, data, { resolveObligeeInformationFromResponse }) {
  return {
    ...prevState,
    basicInformation: {
      firstName: data.guardianshipbond?.ward_first_name,
      lastName: data.guardianshipbond?.ward_last_name,
      middleName: data.guardianshipbond?.ward_middle_name,
      suffix: data.guardianshipbond?.ward_suffix,
      aliases: data.guardianshipbond?.ward_alias,
      bondSubCategory: data.bond_sub_category,
      dateOfDeath: data.guardianshipbond?.ward_date_of_birth,
      bondAmount: Number.parseFloat(data.bond_amount).toString(),
      premiumAmount: data.premium,
      courtCaseNumber: data.court_case_number || "",
      GuardianNumber: data.guardianshipbond?.number_of_guardians,
      is_esignatures: data?.is_esignatures
        ? "Yes"
        : "No",
      county: data.county,
    },
    generalInformation: {
      ongoingBusiness: data.guardianshipbond
        ?.fiduciary_operate_wards_business
        ? "Yes"
        : "No",
      indebtedGuardians: data.guardianshipbond
        ?.guardian_indebited_to_estate
        ? "Yes"
        : "No",
      didLastWillAndTestament: data.guardianshipbond
        ?.ward_execute_last_will_and_testament
        ? "Yes"
        : "No",
      replacingFiduciary: data.guardianshipbond
        ?.guardian_replace_prior_fiduciary
        ? "Yes"
        : "No",
      replacingPriorBond: data.guardianshipbond?.bond_replace_prior_bond
        ? "Yes"
        : "No",
      demandedByInterestedParty: data.guardianshipbond
        ?.replaced_by_non_court
        ? "Yes"
        : "No",
      jointControlExercised: data.guardianshipbond
        ?.joint_control_exercised
        ? "Yes"
        : "No",
      supplementalNeedsTrust: data.guardianshipbond
        ?.supplemental_needs_trust_created
        ? "Yes"
        : "No",
      requestForAuthorization: data.guardianshipbond
        ?.credit_report_allowed
        ? "Yes"
        : "No",
      outstandingLawsuit: data.guardianshipbond
        ?.is_lawsuits_judgements_liens
        ? "Yes"
        : "No",
      bankruptOrInsolvent: data.guardianshipbond?.is_bankrupt_insolvent
        ? "Yes"
        : "No",
      isDeclined: data.guardianshipbond?.is_declined ? "Yes" : "No",
      explanations: {
        ongoingBusiness:
          data.guardianshipbond?.fiduciary_business_explanation || "",
        indebtedGuardians:
          data.guardianshipbond?.guardian_indebted_explanation || "",
        didLastWillAndTestament:
          data.guardianshipbond
            ?.ward_execute_last_will_and_testament_explaination || "",
        replacingFiduciary:
          data.guardianshipbond
            ?.guardian_replace_prior_fiduciary_explaination || "",
        replacingPriorBond:
          data.guardianshipbond?.bond_replace_prior_bond_explaination ||
          "",
        jointControlExercised:
          data.guardianshipbond?.joint_control_explanation || "",
        supplementalNeedsTrust:
          data.guardianshipbond?.supplemental_needs_trust_explanation ||
          "",
        demandedByInterestedParty:
          data.guardianshipbond?.required_non_court_explanation || "",
        outstandingLawsuitExplanation:
          data.guardianshipbond
            ?.is_lawsuits_judgements_liens_explanation || "",
        bankruptOrInsolventExplanation:
          data.guardianshipbond?.is_bankrupt_insolvent_explanation || "",
        isDeclined: data.guardianshipbond?.is_declined_explanation || "",
      },
    },
    guardianDetails: {
      guardians: data.guardians.map(mapApiGuardianToGuardian),
    },
    attorneyInformation: {
      attorneyInvolved: data.law_firm || data.guest_lawfirm_name ? "Yes" : "No",
      lawFirm: data.law_firm,
      lawFirmAddress: data.law_firm_location,
      attorneyOfRecord: data.law_firm_user,
      guest_lawfirm_name: data.guest_lawfirm_name,
      guest_lawfirmaddress_name: data.guest_lawfirmaddress_name,
      guest_attorney_name: data.guest_attorney_name,
      is_guest: data.is_guest,
      referral_source: data.referral_source,
      referred_by: data.referred_by,
    },
    obligeeInformation: resolveObligeeInformationFromResponse(data),
    requestorInformation: {
      email: data.requester_user_email || "No email",
      phoneNumber: data.requester_phone_number || "No phone number",
      firstName: data.requester_name,
    },
    documentUpload: {
      lastWillFileName:
        data.guardianshipbond?.copy_of_last_will_and_testament ||
        "No file uploaded",
      lastWillLink:
        data.guardianshipbond?.copy_of_last_will_and_testament_link || "",
      otherDocumentFileName:
        data.guardianshipbond?.copy_of_supplemental_needs_trust ||
        "No file uploaded",
      otherDocumentLink:
        data.guardianshipbond?.copy_of_supplemental_needs_trust_link ||
        "",
      courtOrderFileName:
        data.guardianshipbond?.copy_of_court_order || "No file uploaded",
      courtOrderLink:
        data.guardianshipbond?.copy_of_court_order_link || "",
      bondAppFileName: data.first_document_name || "No file uploaded",
      bondAppLink: data.first_document_url || "",
      bondFileName: data.second_document_name || "No file uploaded",
      bondLink: data.second_document_url || "",
      indemnityFileName: data.third_document_name || "No file uploaded",
      indemnityLink: data.third_document_url || "",
      cancellationDocumentFileName:
        data.guardianshipbond?.cancellation_document ||
        "No file uploaded",
      cancellationDocumentLink:
        data.guardianshipbond?.cancellation_document_link || "",
      endorsementDocumentFileName:
        data.guardianshipbond?.endorsement_document || "No file uploaded",
      endorsementDocumentLink:
        data.guardianshipbond?.endorsement_document_link || "",
      endorsementPOADocumentFileName:
        data.guardianshipbond?.endorsement_poa_document || "No file uploaded",
      endorsementPOADocumentLink:
        data.guardianshipbond?.endorsement_poa_document_link || "",
      note: data.guardianshipbond?.notes || "",
      creditDocumentFileName:
        data.guardianshipbond?.credit_report_document ||
        "No file uploaded",
      creditDocumentLink:
        data.guardianshipbond?.credit_report_document_link || "",
      fcraDocumentFileName:
        data.guardianshipbond?.fcra_document || "No file uploaded",
      fcraDocumentLink:
        data.guardianshipbond?.fcra_document_link || "",
    },
    billingInformation: {
      billing_entity: data.guardianshipbond?.billing_entity || "Unknown",
      billing_lawfirmaddress_name:
        data.guardianshipbond?.billing_lawfirmaddress_name || "",
      billing_principal_name:
        data.guardianshipbond?.billing_principal_name || "Unknown",
      billing_other_name: data.guardianshipbond?.billing_other_name || "",
      billing_other_address:
        data.guardianshipbond?.billing_other_address || "",
      billing_other_relationship:
        data.guardianshipbond?.billing_other_relationship || "",
      billing_other_city: data.guardianshipbond?.billing_other_city,
      billing_other_state: data.guardianshipbond?.billing_other_state,
      billing_other_zip: data.guardianshipbond?.billing_other_zip,
      billing_method: data.guardianshipbond?.billing_method ?? data.billing_method ?? "",
      custNo: data.guardianshipbond?.other_custno || "",
    },
    bondNumber: {
      bond_number: data.bond_number,
    },
  };
}

/** @param {{ resolveObligeeInformationFromResponse: (apiData:any)=>any }} ctx */
export function mergeProbateRetrieveResponse(prevState, data, { resolveObligeeInformationFromResponse }) {
  return {
    ...prevState,
    basicInformation: {
      firstName: data.probatebond?.deceased_legal_first_name,
      lastName: data.probatebond?.deceased_legal_last_name,
      middleName: data.probatebond?.deceased_legal_middle_name,
      suffix: data.probatebond?.deceased_legal_suffix,
      aliases: data.probatebond?.alias_deceased_legal_name,
      bondSubCategory: data.bond_sub_category,
      judgeStern: data.probatebond?.is_judge_stearn ? "Yes" : "No",
      wrongfulDeathLawsuit: data.probatebond?.is_wrongful_death
        ? "Yes"
        : "No",
      dateOfDeath: data.probatebond?.date_of_death,
      bondAmount: Number.parseFloat(data.bond_amount).toString(),
      premiumAmount: data.premium,
      courtCaseNumber: data.court_case_number,
      PrincipalNumber: resolvePrincipalNumberForRetrieve(
        data.probatebond?.number_of_personal_representative,
        data.personal_rep?.length
      ),
      is_esignatures: data?.is_esignatures
        ? "Yes"
        : "No",
      county: data.county,
    },
    generalInformation: {
      estAssets: data.probatebond?.estimated_estate_assets,
      ongoingBusiness: data.probatebond?.estate_include_ongoing_business
        ? "Yes"
        : "No",
      indebtedGuardians: data.probatebond?.personal_rep_indebted
        ? "Yes"
        : "No",
      didLastWillAndTestament: data.probatebond
        ?.execute_last_will_testament
        ? "Yes"
        : "No",
      replacingFiduciary: data.probatebond?.replace_prior_fiduciary
        ? "Yes"
        : "No",
      replacingPriorBond: data.probatebond?.replace_prior_bond
        ? "Yes"
        : "No",
      demandedByInterestedParty: data.probatebond?.demand_other_than_court
        ? "Yes"
        : "No",
      requestForAuthorization: data.probatebond?.credit_report_allowed
        ? "Yes"
        : "No",
      outstandingLawsuit: data.probatebond?.outstanding_lawsuits
        ? "Yes"
        : "No",
      bankruptOrInsolvent: data.probatebond?.ever_bankrupt_insolvent
        ? "Yes"
        : "No",
      isDeclined: data.probatebond?.declined_by_any_other_surety
        ? "Yes"
        : "No",
      anyDisputes: data.probatebond?.any_disputes ? "Yes" : "No",
      explanations: {
        ongoingBusiness:
          data.probatebond?.estate_include_ongoing_business_explanation ||
          "",
        replacingFiduciary:
          data.probatebond?.replace_prior_fiduciary_explanation || "",
        demandedByInterestedParty:
          data.probatebond?.demand_other_than_court_explanation || "",
        indebtedGuardians:
          data.probatebond?.personal_rep_indebted_explanation || "",
        isDeclined:
          data.probatebond?.declined_by_any_other_surety_explanation ||
          "",
        outstandingLawsuitExplanation:
          data.probatebond?.outstanding_lawsuits_explanation || "",
        bankruptOrInsolventExplanation:
          data.probatebond?.ever_bankrupt_insolvent_explanation || "",
        anyDisputes: data.probatebond?.any_disputes_explanation || "",
      },
    },
    guardianDetails: {
      guardians: data.personal_rep.map(mapApiPersonalRepToGuardian),
    },
    attorneyInformation: {
      attorneyInvolved: data.law_firm || data.guest_lawfirm_name ? "Yes" : "No",
      lawFirm: data.law_firm,
      lawFirmAddress: data.law_firm_location,
      attorneyOfRecord: data.law_firm_user,
      guest_lawfirm_name: data.guest_lawfirm_name,
      guest_lawfirmaddress_name: data.guest_lawfirmaddress_name,
      guest_attorney_name: data.guest_attorney_name,
      is_guest: data.is_guest,
      referral_source: data.referral_source,
      referred_by: data.referred_by,
    },
    obligeeInformation: resolveObligeeInformationFromResponse(data),
    requestorInformation: {
      email: data.requester_user_email,
      phoneNumber: data.requester_phone_number,
      firstName: data.requester_name,
    },
    documentUpload: {
      lastWillFileName:
        data.probatebond?.copy_of_last_will_and_testament ||
        "No file uploaded",
      lastWillLink:
        data.probatebond?.copy_of_last_will_and_testament_link || "",
      otherDocumentFileName:
        data.probatebond?.other_document || "No file uploaded",
      otherDocumentLink: data.probatebond?.other_document_link || "",
      courtOrderFileName:
        data.probatebond?.copy_of_court_order || "No file uploaded",
      courtOrderLink: data.probatebond?.copy_of_court_order_link || "",
      bondAppFileName: data.first_document_name || "No file uploaded",
      bondAppLink: data.first_document_url || "",
      bondFileName: data.second_document_name || "No file uploaded",
      bondLink: data.second_document_url || "",
      indemnityFileName: data.third_document_name || "No file uploaded",
      indemnityLink: data.third_document_url || "",
      cancellationDocumentFileName:
        data.probatebond?.cancellation_document || "No file uploaded",
      cancellationDocumentLink:
        data.probatebond?.cancellation_document_link || "",
      note: data.probatebond?.notes || "",
      endorsementDocumentFileName:
        data.probatebond?.endorsement_document || "No file uploaded",
      endorsementDocumentLink:
        data.probatebond?.endorsement_document_link || "",
      endorsementPOADocumentFileName:
        data.probatebond?.endorsement_poa_document || "No file uploaded",
      endorsementPOADocumentLink:
        data.probatebond?.endorsement_poa_document_link || "",
      creditDocumentFileName:
        data.probatebond?.credit_report_document || "No file uploaded",
      creditDocumentLink:
        data.probatebond?.credit_report_document_link || "",
      fcraDocumentFileName:
        data.probatebond?.fcra_document || "No file uploaded",
      fcraDocumentLink:
        data.probatebond?.fcra_document_link || "",
    },
    billingInformation: {
      billing_entity: data.probatebond?.billing_entity,
      billing_lawfirmaddress_name:
        data.probatebond?.billing_lawfirmaddress_name,
      billing_principal_name: data.probatebond?.billing_principal_name,
      billing_other_name: data.probatebond?.billing_other_name,
      billing_other_address: data.probatebond?.billing_other_address,
      billing_other_relationship:
        data.probatebond?.billing_other_relationship,
      billing_other_city: data.probatebond?.billing_other_city,
      billing_other_state: data.probatebond?.billing_other_state,
      billing_other_zip: data.probatebond?.billing_other_zip,
      billing_method:
        data.probatebond?.billing_method ?? data.billing_method ?? "",
      custNo: data.probatebond?.other_custno,
    },
    bondNumber: {
      bond_number: data.bond_number,
    },
  };
}

/**
 * @param {string} kind
 * @param {object} prevState
 * @param {object} data
 * @param {Record<string, unknown>} ctx resolvers keyed per kind
 */
export function mapRetrieveResponseToGlobalStateMerge(kind, prevState, data, ctx) {
  if (!bondRetrievalPageConfig[kind]) throw new Error(`Unknown bond retrieval kind: ${kind}`);
  if (kind === "va") {
    return mergeVaRetrieveResponse(prevState, data, {
      resolveVaObligeeInformation: ctx.resolveVaObligeeInformation,
    });
  }
  if (kind === "trustee") {
    return mergeTrusteeRetrieveResponse(prevState, data, {
      resolveObligeeInformationFromResponse: ctx.resolveObligeeInformationFromResponse,
    });
  }
  if (kind === "guardianship") {
    return mergeGuardianshipRetrieveResponse(prevState, data, {
      resolveObligeeInformationFromResponse: ctx.resolveObligeeInformationFromResponse,
    });
  }
  if (kind === "probate") {
    return mergeProbateRetrieveResponse(prevState, data, {
      resolveObligeeInformationFromResponse: ctx.resolveObligeeInformationFromResponse,
    });
  }
  throw new Error(`Unhandled retrieval kind: ${kind}`);
}

/**
 * Count fields for getRetrievePrincipalCount from a bond retrieve API body (approve/rider).
 * Uses stored DB counts only — not max(stored, row length).
 */
export function basicInformationCountsFromRetrieveApi(data, bondKind) {
  if (bondKind === "guardianship") {
    return {
      GuardianNumber: data?.guardianshipbond?.number_of_guardians,
    };
  }
  if (bondKind === "va") {
    return {
      PrincipalNumber:
        data?.vabond?.number_of_principals ??
        data?.vabond?.number_of_prinicipals,
    };
  }
  if (bondKind === "probate") {
    return {
      PrincipalNumber: data?.probatebond?.number_of_personal_representative,
    };
  }
  if (bondKind === "trustee") {
    return {
      PrincipalNumber: data?.trustee_bond?.number_of_principals,
    };
  }
  return {};
}

/**
 * Trim fiduciary lists on approve POST to match stored principal/guardian count.
 * Used by Release Rider approve (same rules as bond retrieval approve).
 */
export function applyFiduciarySliceForApproveBody(retrieveData, bondKind) {
  if (!retrieveData || !bondKind) {
    return retrieveData;
  }

  const basicInformation = basicInformationCountsFromRetrieveApi(
    retrieveData,
    bondKind
  );

  if (bondKind === "guardianship") {
    const guardians = guardianshipGuardiansForApprovePayload(
      retrieveData,
      basicInformation
    );
    if (guardians) {
      return { ...retrieveData, guardians };
    }
    return retrieveData;
  }

  const kindConfig = getBondPayloadKindConfig(bondKind);
  if (!kindConfig) {
    return retrieveData;
  }

  const principalsKey = kindConfig.principalsKey;
  const list = retrieveData[principalsKey];
  if (!Array.isArray(list) || list.length === 0) {
    return retrieveData;
  }

  const count = getRetrievePrincipalCount(basicInformation, bondKind);
  if (list.length <= count) {
    return retrieveData;
  }

  return {
    ...retrieveData,
    [principalsKey]: list.slice(0, count),
  };
}

/** Sync guardian rows (and guardian_id) from PATCH response after review saves or pre-approve update. */
export function mergeGuardianshipGuardiansFromPatchResponse(prevGlobalState, patchResponse) {
  const refreshed = guardianshipGuardiansFromPatchResponse(
    patchResponse,
    prevGlobalState.basicInformation
  );
  if (!refreshed) {
    return prevGlobalState;
  }
  return {
    ...prevGlobalState,
    guardianDetails: {
      ...prevGlobalState.guardianDetails,
      guardians: refreshed,
    },
  };
}

export function guardianshipGuardiansFromPatchResponse(patchResponse, basicInformation) {
  if (!Array.isArray(patchResponse?.guardians) || patchResponse.guardians.length === 0) {
    return null;
  }
  const count = getRetrievePrincipalCount(basicInformation, "guardianship");
  return patchResponse.guardians.map(mapApiGuardianToGuardian).slice(0, count);
}

/** Approve POST body: same slice + citizenship normalization as retrieve payload builder. */
export function guardianshipGuardiansForApprovePayload(patchResponse, basicInformation) {
  const mapped = guardianshipGuardiansFromPatchResponse(patchResponse, basicInformation);
  if (!mapped) {
    return null;
  }
  return normalizeGuardiansForPayload(mapped);
}

export function applyFcraOverrideToPayload(payload, nestedKey, fcraOverride) {
  if (fcraOverride?.fcraDocumentFileName == null && fcraOverride?.fcraDocumentLink == null) {
    return;
  }
  const nested = payload[nestedKey];
  if (!nested) return;
  nested.fcra_document = fcraOverride.fcraDocumentFileName ?? nested.fcra_document;
  nested.fcra_document_link = fcraOverride.fcraDocumentLink ?? nested.fcra_document_link;
}
